import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAudit } from "../context/AuditContext";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import API from "../lib/api";
import {
  ClipboardCheck, ArrowRight, Loader2, ChevronLeft, ChevronRight, Search,
  Database, FileText, Unplug, Play, CheckCircle2, XCircle, Circle, StopCircle,
} from "lucide-react";
import { PageLayout } from "../components/PageLayout";
import ControlTestingBoard from "../components/ControlTestingBoard";

const PAGE_SIZE = 20;
const BATCH_CONCURRENCY = 3;
const POLL_MS = 1500;
const MAX_POLLS = 120;

/**
 * Controls are not equally testable, and treating them as one flat list is why
 * this page is unusable at 260 controls. Three groups, decided by what the
 * control actually has behind it:
 *
 *   data      linked artefact tables, so a SQL or reconciliation test can run
 *   document  evaluated against documents through RAG, no transaction data needed
 *   unmapped  expects data but has no artefact linked, so computing it will
 *             never produce a result until someone maps a source
 *
 * The third group is the one that wastes an auditor's afternoon. It is now
 * visible and excluded from batch runs rather than silently failing.
 */
const GROUPS = [
  {
    key: "data",
    label: "Data-backed",
    icon: Database,
    blurb: "Artefact tables are linked. These can be tested against transaction data.",
    tone: "text-emerald-600 dark:text-emerald-400",
    ring: "border-emerald-500/40 bg-emerald-500/5",
  },
  {
    key: "document",
    label: "Document-based",
    icon: FileText,
    blurb: "Evaluated against uploaded documents. No transaction data required.",
    tone: "text-indigo-600 dark:text-indigo-400",
    ring: "border-indigo-500/40 bg-indigo-500/5",
  },
  {
    key: "unmapped",
    label: "No data linked",
    icon: Unplug,
    blurb: "These expect transaction data but no artefact table is linked, so testing cannot produce a result yet. Link a data source in Phase 5, or reclassify the control as document-based.",
    tone: "text-amber-600 dark:text-amber-400",
    ring: "border-amber-500/40 bg-amber-500/5",
  },
];

const classifyControl = (c) => {
  const sources = Array.isArray(c?.data_sources) ? c.data_sources.filter(Boolean) : [];
  const type = String(c?.check_type || "").toUpperCase();
  if (sources.length > 0) return "data";
  if (type === "RAG" || type === "DOCUMENT" || type === "MANUAL") return "document";
  return "unmapped";
};

export default function Phase7Testing() {
  const navigate = useNavigate();
  const { auditRun, advancePhase, controls = [] } = useAudit();

  const [group, setGroup] = useState("data");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [selectedControlId, setSelectedControlId] = useState("");
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);

  // controlId -> "idle" | "queued" | "running" | "done" | "failed" | "none"
  const [status, setStatus] = useState({});
  const [batchRunning, setBatchRunning] = useState(false);
  const [batchProgress, setBatchProgress] = useState({ done: 0, total: 0 });
  const abortRef = useRef(false);
  const fetchedRef = useRef(new Set());

  const setControlStatus = useCallback((id, value) => {
    setStatus((prev) => (prev[id] === value ? prev : { ...prev, [id]: value }));
  }, []);

  const inScopeControls = useMemo(
    () => (controls || []).filter((c) => Boolean(c.in_scope)),
    [controls]
  );

  const grouped = useMemo(() => {
    const buckets = { data: [], document: [], unmapped: [] };
    inScopeControls.forEach((c) => buckets[classifyControl(c)].push(c));
    return buckets;
  }, [inScopeControls]);

  const filtered = useMemo(() => {
    const list = grouped[group] || [];
    const q = search.trim().toLowerCase();
    if (!q) return list;
    return list.filter((c) =>
      [c.control_id, c.id, c.description, c.mapped_process_area, c.clause_reference]
        .filter(Boolean).join(" ").toLowerCase().includes(q));
  }, [grouped, group, search]);

  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.max(0, Math.min(page, pageCount - 1));
  const visible = useMemo(
    () => filtered.slice(safePage * PAGE_SIZE, safePage * PAGE_SIZE + PAGE_SIZE),
    [filtered, safePage]
  );

  useEffect(() => { setPage(0); }, [group, search]);

  const fetchReport = useCallback(async (ctrlId) => {
    if (!ctrlId || !auditRun?.id) return null;
    setLoading(true);
    try {
      const { data } = await API.get(`/audit-runs/${auditRun.id}/controls/${ctrlId}/testing`);
      if (data?.available) {
        setReport(data);
        return data;
      }
      setReport(null);
      return null;
    } catch {
      setReport(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, [auditRun?.id]);

  // Only the controls on screen are checked, and each control is requested at most ONCE
  // using fetchedRef to prevent duplicate API triggers across component re-renders.
  useEffect(() => {
    if (!auditRun?.id || visible.length === 0) return;

    const toFetch = visible.filter((c) => c?.id && !fetchedRef.current.has(c.id));
    if (toFetch.length === 0) return;

    // Immediately mark control IDs as fetched/in-flight to avoid duplicate calls
    toFetch.forEach((c) => fetchedRef.current.add(c.id));

    let cancelled = false;
    (async () => {
      await Promise.all(
        toFetch.map(async (c) => {
          try {
            const { data } = await API.get(`/audit-runs/${auditRun.id}/controls/${c.id}/testing`);
            if (!cancelled) setControlStatus(c.id, data?.available ? "done" : "none");
          } catch {
            if (!cancelled) setControlStatus(c.id, "none");
          }
        })
      );
    })();
    return () => { cancelled = true; };
  }, [auditRun?.id, visible, setControlStatus]);

  useEffect(() => {
    if (selectedControlId) fetchReport(selectedControlId);
    else setReport(null);
  }, [selectedControlId, fetchReport]);

  const computeOne = useCallback(async (ctrlId) => {
    if (!ctrlId || !auditRun?.id) return false;
    setControlStatus(ctrlId, "running");
    try {
      const { data } = await API.post(
        `/audit-runs/${auditRun.id}/controls/${ctrlId}/testing/compute`,
      );
      const jobId = data?.job_id;
      if (!jobId) {
        setControlStatus(ctrlId, "failed");
        return false;
      }
      for (let attempt = 0; attempt < MAX_POLLS; attempt += 1) {
        if (abortRef.current) {
          setControlStatus(ctrlId, "idle");
          return false;
        }
        await new Promise((resolve) => setTimeout(resolve, POLL_MS));
        try {
          const { data: jobStatus } = await API.get(`/job-status/${jobId}`);
          if (jobStatus.state === "SUCCESS") {
            setControlStatus(ctrlId, "done");
            return true;
          }
          if (jobStatus.state === "FAILURE") {
            setControlStatus(ctrlId, "failed");
            return false;
          }
        } catch {
          setControlStatus(ctrlId, "failed");
          return false;
        }
      }
      setControlStatus(ctrlId, "failed");
      return false;
    } catch (e) {
      setControlStatus(ctrlId, "failed");
      return false;
    }
  }, [auditRun?.id, setControlStatus]);

  const handleComputeSingle = async (ctrlId) => {
    setSelectedControlId(ctrlId);
    const ok = await computeOne(ctrlId);
    if (ok) {
      await fetchReport(ctrlId);
      toast.success("Testing report complete");
    } else {
      toast.error("Computation failed for this control");
    }
  };

  const runBatch = async (targets) => {
    if (targets.length === 0) return;
    abortRef.current = false;
    setBatchRunning(true);
    setBatchProgress({ done: 0, total: targets.length });
    targets.forEach((c) => setControlStatus(c.id, "queued"));

    const queue = [...targets];
    let completed = 0;

    const worker = async () => {
      while (queue.length > 0 && !abortRef.current) {
        const control = queue.shift();
        if (!control) break;
        await computeOne(control.id);
        completed += 1;
        setBatchProgress({ done: completed, total: targets.length });
      }
    };

    await Promise.all(
      Array.from({ length: Math.min(BATCH_CONCURRENCY, targets.length) }, worker),
    );

    setBatchRunning(false);
    if (abortRef.current) {
      toast.info(`Stopped after ${completed} of ${targets.length} controls.`);
      queue.forEach((c) => setControlStatus(c.id, "idle"));
    } else {
      toast.success(`Tested ${completed} control${completed === 1 ? "" : "s"}.`);
    }
    if (selectedControlId) await fetchReport(selectedControlId);
  };

  const untestedInGroup = useMemo(
    () => filtered.filter((c) => status[c.id] !== "done"),
    [filtered, status],
  );

  const handleContinue = async () => {
    await advancePhase(8);
    navigate("/phase/8");
  };

  const statusBadge = (state) => {
    if (state === "done") {
      return <Badge variant="outline" className="text-[10px] bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/30"><CheckCircle2 className="w-3 h-3 mr-1" />Tested</Badge>;
    }
    if (state === "failed") {
      return <Badge variant="outline" className="text-[10px] bg-red-500/10 text-red-500 border-red-500/30"><XCircle className="w-3 h-3 mr-1" />Failed</Badge>;
    }
    if (state === "running") {
      return <Badge variant="outline" className="text-[10px] bg-indigo-500/10 text-indigo-500 border-indigo-500/30"><Loader2 className="w-3 h-3 mr-1 animate-spin" />Running</Badge>;
    }
    if (state === "queued") {
      return <Badge variant="outline" className="text-[10px] bg-slate-500/10 text-muted-foreground border-slate-400/30"><Circle className="w-3 h-3 mr-1" />Queued</Badge>;
    }
    return <Badge variant="outline" className="text-[10px] text-muted-foreground"><Circle className="w-3 h-3 mr-1" />Not tested</Badge>;
  };

  const activeGroup = GROUPS.find((g) => g.key === group) || GROUPS[0];

  return (
    <PageLayout
      icon={ClipboardCheck}
      title="Control Testing"
      subtitle="Design Effectiveness, Operating Effectiveness, and Outcome Testing per control."
      actions={
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => navigate("/phase/7")}>
            <ChevronLeft className="w-3.5 h-3.5 mr-1" /> Back to Evidence
          </Button>
          <Button
            onClick={handleContinue}
            size="sm"
            className="rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs h-9 px-4 shadow-md shadow-emerald-600/30 active:scale-95 border-0"
            data-testid="continue-to-findings-top-btn"
          >
            Continue to Findings <ArrowRight className="w-4 h-4 ml-1.5" />
          </Button>
        </div>
      }
    >
      {/* Group selector. The counts alone answer the question an auditor asks
          first: how much of this can actually be tested? */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-5">
        {GROUPS.map((g) => {
          const Icon = g.icon;
          const count = grouped[g.key].length;
          const tested = grouped[g.key].filter((c) => status[c.id] === "done").length;
          const active = group === g.key;
          return (
            <button
              key={g.key}
              type="button"
              onClick={() => setGroup(g.key)}
              className={`text-left p-4 rounded-xl border transition-all ${
                active ? `${g.ring} ring-2 ring-offset-2 ring-offset-background ring-indigo-500/40`
                       : "border-slate-200 dark:border-white/[0.08] hover:bg-slate-50 dark:hover:bg-white/[0.03]"
              }`}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Icon className={`w-4 h-4 ${g.tone}`} />
                  <span className="text-sm font-semibold">{g.label}</span>
                </div>
                <span className="font-mono text-lg font-bold">{count}</span>
              </div>
              <p className="text-[11px] text-muted-foreground mt-1.5 leading-snug">{g.blurb}</p>
              {g.key !== "unmapped" && count > 0 && (
                <p className="text-[10px] text-muted-foreground mt-2 font-mono">{tested} of {count} tested</p>
              )}
            </button>
          );
        })}
      </div>

      <Card className="mb-5">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <activeGroup.icon className={`w-4 h-4 ${activeGroup.tone}`} />
            {activeGroup.label} ({filtered.length})
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative flex-1 min-w-[220px]">
              <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search control id, description or process area"
                className="h-8 pl-8 text-xs"
              />
            </div>

            {group === "unmapped" ? (
              <span className="text-[11px] text-amber-600 dark:text-amber-400">
                Batch testing is disabled for this group. Link a data source first.
              </span>
            ) : batchRunning ? (
              <>
                <span className="text-xs font-mono text-muted-foreground">
                  {batchProgress.done} / {batchProgress.total}
                </span>
                <Button variant="outline" size="sm" onClick={() => { abortRef.current = true; }}>
                  <StopCircle className="w-3.5 h-3.5 mr-1.5" /> Stop
                </Button>
              </>
            ) : (
              <>
                <Button size="sm" onClick={() => runBatch(untestedInGroup)} disabled={untestedInGroup.length === 0}>
                  <Play className="w-3.5 h-3.5 mr-1.5" />
                  Test {untestedInGroup.length} untested
                </Button>
                <Button variant="outline" size="sm" onClick={() => runBatch(visible)} disabled={visible.length === 0}>
                  Test this page
                </Button>
              </>
            )}
          </div>

          {batchRunning && (
            <div className="h-1.5 w-full rounded-full bg-slate-200 dark:bg-white/[0.08] overflow-hidden">
              <div
                className="h-full bg-indigo-500 transition-all"
                style={{ width: `${batchProgress.total ? (batchProgress.done / batchProgress.total) * 100 : 0}%` }}
              />
            </div>
          )}

          {filtered.length === 0 && (
            <p className="text-xs text-muted-foreground text-center py-8">
              No controls in this group{search ? " match your search" : ""}.
            </p>
          )}

          <div className="divide-y divide-slate-100 dark:divide-white/[0.06]">
            {visible.map((c) => {
              const state = status[c.id] || "idle";
              const selected = c.id === selectedControlId;
              return (
                <div
                  key={c.id}
                  className={`flex flex-col sm:flex-row sm:items-center gap-3 py-3 px-2 -mx-2 rounded-lg cursor-pointer transition-colors ${
                    selected ? "bg-indigo-50 dark:bg-indigo-950/20" : "hover:bg-slate-50 dark:hover:bg-white/[0.03]"
                  }`}
                  onClick={() => setSelectedControlId(c.id)}
                >
                  <span className="font-mono text-[10px] font-bold text-indigo-600 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-950/40 px-2 py-0.5 rounded shrink-0 w-fit">
                    {c.control_id || c.id}
                  </span>
                  <p className="text-xs text-slate-700 dark:text-slate-300 flex-1 min-w-0 leading-snug">
                    {c.description || "No description"}
                  </p>
                  {Array.isArray(c.data_sources) && c.data_sources.length > 0 && (
                    <span className="text-[10px] font-mono text-muted-foreground shrink-0">
                      {c.data_sources.join(", ")}
                    </span>
                  )}
                  <div className="shrink-0">{statusBadge(state)}</div>
                  <Button
                    size="sm"
                    variant={state === "done" ? "outline" : "default"}
                    className="h-7 px-2.5 text-xs shrink-0"
                    disabled={state === "running" || state === "queued" || batchRunning || group === "unmapped"}
                    onClick={(e) => { e.stopPropagation(); handleComputeSingle(c.id); }}
                  >
                    {state === "running" ? <Loader2 className="w-3 h-3 animate-spin" />
                      : state === "done" ? "Re-compute" : "Compute"}
                  </Button>
                </div>
              );
            })}
          </div>

          {pageCount > 1 && (
            <div className="flex items-center justify-between pt-2">
              <Button variant="outline" size="sm" onClick={() => setPage((p) => Math.max(0, p - 1))} disabled={safePage === 0}>
                <ChevronLeft className="w-3.5 h-3.5 mr-1" /> Previous
              </Button>
              <span className="text-xs text-muted-foreground">Page {safePage + 1} of {pageCount}</span>
              <Button variant="outline" size="sm" onClick={() => setPage((p) => Math.min(pageCount - 1, p + 1))} disabled={safePage >= pageCount - 1}>
                Next <ChevronRight className="w-3.5 h-3.5 ml-1" />
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {selectedControlId ? (
        <ControlTestingBoard
          report={report}
          loading={loading}
          onRecompute={() => handleComputeSingle(selectedControlId)}
          onOverride={() => toast.info("Override: re-compute to regenerate, or edit fields directly in a future release.")}
          disableRecompute={batchRunning || status[selectedControlId] === "running"}
        />
      ) : (
        <Card>
          <CardContent className="py-10 text-center text-sm text-muted-foreground">
            Select a control above to see its testing report.
          </CardContent>
        </Card>
      )}
    </PageLayout>
  );
}












// import { useState, useEffect, useCallback } from "react";
// import { useNavigate } from "react-router-dom";
// import { useAudit } from "../context/AuditContext";
// import { Button } from "../components/ui/button";
// import { Card, CardContent } from "../components/ui/card";
// import {
//   Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
// } from "../components/ui/select";
// import { Label } from "../components/ui/label";
// import { toast } from "sonner";
// import API from "../lib/api";
// import {
//   ClipboardCheck, ArrowRight, Loader2, ChevronLeft,
// } from "lucide-react";
// import { PageLayout } from "../components/PageLayout";
// import ControlTestingBoard from "../components/ControlTestingBoard";

// export default function Phase7Testing() {
//   const navigate = useNavigate();
//   const { auditRun, advancePhase, controls = [] } = useAudit();
//   const [selectedControlId, setSelectedControlId] = useState("");
//   const [report, setReport] = useState(null);
//   const [loading, setLoading] = useState(false);
//   const [computing, setComputing] = useState(false);

//   const fetchReport = useCallback(async (ctrlId) => {
//     if (!ctrlId || !auditRun?.id) return;
//     setLoading(true);
//     try {
//       const { data } = await API.get(`/audit-runs/${auditRun.id}/controls/${ctrlId}/testing`);
//       if (data?.available) {
//         setReport(data);
//       } else {
//         setReport(null);
//       }
//     } catch {
//       setReport(null);
//     } finally {
//       setLoading(false);
//     }
//   }, [auditRun?.id]);

//   useEffect(() => {
//     if (selectedControlId) {
//       fetchReport(selectedControlId);
//     } else {
//       setReport(null);
//     }
//   }, [selectedControlId, fetchReport]);

//   const handleCompute = async () => {
//     if (!selectedControlId || !auditRun?.id) return;
//     setComputing(true);
//     try {
//       const { data } = await API.post(
//         `/audit-runs/${auditRun.id}/controls/${selectedControlId}/testing/compute`,
//       );
//       const jobId = data?.job_id;
//       if (!jobId) {
//         toast.error("Failed to start computation");
//         setComputing(false);
//         return;
//       }

//       toast.success("Computing control testing report...");

//       const pollInterval = 1500;
//       const maxAttempts = 120;
//       let attempts = 0;

//       const poll = async () => {
//         try {
//           const { data: status } = await API.get(`/job-status/${jobId}`);
//           if (status.state === "SUCCESS") {
//             setComputing(false);
//             await fetchReport(selectedControlId);
//             toast.success("Testing report complete");
//           } else if (status.state === "FAILURE") {
//             setComputing(false);
//             toast.error(status.error || "Computation failed");
//           } else {
//             attempts += 1;
//             if (attempts >= maxAttempts) {
//               setComputing(false);
//               toast.error("Computation timed out");
//               return;
//             }
//             setTimeout(poll, pollInterval);
//           }
//         } catch {
//           setComputing(false);
//           toast.error("Failed to check computation status");
//         }
//       };

//       setTimeout(poll, pollInterval);
//     } catch (e) {
//       const msg = e?.response?.data?.detail || e?.message || "Computation failed";
//       toast.error(msg);
//       setComputing(false);
//     }
//   };

//   const handleOverride = async () => {
//     if (!selectedControlId || !auditRun?.id || !report) return;
//     toast.info("Override: Click 'Re-compute' to regenerate, or edit fields directly in a future release.");
//   };

//   const handleContinue = async () => {
//     await advancePhase(8);
//     navigate("/phase/8");
//   };

//   const selectedControl = controls.find((c) => c.id === selectedControlId);

//   return (
//     <PageLayout
//       icon={ClipboardCheck}
//       title="Control Testing"
//       subtitle="Design Effectiveness, Operating Effectiveness, and Outcome Testing per control."
//       actions={
//         <div className="flex items-center gap-2">
//           <Button
//             variant="outline"
//             size="sm"
//             onClick={() => navigate("/phase/7")}
//           >
//             <ChevronLeft className="w-3.5 h-3.5 mr-1" />
//             Back to Evidence
//           </Button>
//           <Button
//             onClick={handleContinue}
//             size="sm"
//           >
//             <ArrowRight className="w-4 h-4 mr-2" />
//             Continue to Findings
//           </Button>
//         </div>
//       }
//     >
//       <Card className="mb-6">
//         <CardContent className="py-4">
//           <div className="flex items-end gap-4">
//             <div className="flex-1 min-w-0 space-y-1.5">
//               <Label htmlFor="control-select">Select Control</Label>
//               <Select
//                 value={selectedControlId}
//                 onValueChange={setSelectedControlId}
//               >
//                 <SelectTrigger id="control-select" className="w-full">
//                   <SelectValue placeholder="Choose a control to test..." />
//                 </SelectTrigger>
//                 <SelectContent className="w-[var(--radix-select-trigger-width)]">
//                   {controls.map((c) => (
//                     <SelectItem key={c.id} value={c.id} className="whitespace-nowrap overflow-hidden text-ellipsis max-w-full">
//                       <span className="font-medium">{c.control_id || c.id}</span>
//                       <span className="text-muted-foreground ml-1.5">— {c.description || ""}</span>
//                     </SelectItem>
//                   ))}
//                 </SelectContent>
//               </Select>
//             </div>
//             <Button
//               onClick={handleCompute}
//               disabled={!selectedControlId || computing}
//             >
//               {computing ? (
//                 <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
//               ) : (
//                 <ClipboardCheck className="w-3.5 h-3.5 mr-1.5" />
//               )}
//               {computing ? "Computing..." : "Compute"}
//             </Button>
//           </div>
//         </CardContent>
//       </Card>

//       <ControlTestingBoard
//         report={report}
//         loading={loading}
//         onRecompute={handleCompute}
//         onOverride={handleOverride}
//         disableRecompute={computing}
//       />
//     </PageLayout>
//   );
// }
